const API_BASE_URL = process.env.REACT_APP_API_URL;
if (!API_BASE_URL) {
  throw new Error('REACT_APP_API_URL environment variable is required');
}

export interface Feedback {
  id: string;
  user_id: string;
  subject: string;
  message: string;
  image_url?: string;
  status: 'pending' | 'reviewed' | 'resolved';
  admin_notes?: string;
  created_at: string;
  updated_at?: string;
}

export interface FeedbackCreate {
  subject: string;
  message: string;
  image_url?: string;
}

export interface FeedbackWithUser extends Feedback {
  user_email?: string;
  user_first_name?: string;
  user_last_name?: string;
}

class FeedbackService {
  private getAuthHeaders() {
    const token = localStorage.getItem('token');
    return {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${token}`
    };
  }

  async createFeedback(feedback: FeedbackCreate): Promise<Feedback> {
    const response = await fetch(`${API_BASE_URL}/api/feedback`, {
      method: 'POST',
      headers: this.getAuthHeaders(),
      body: JSON.stringify(feedback)
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.detail || 'Failed to create feedback');
    }

    return response.json();
  }

  async getMyFeedback(): Promise<Feedback[]> {
    const response = await fetch(`${API_BASE_URL}/api/feedback`, {
      method: 'GET',
      headers: this.getAuthHeaders()
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.detail || 'Failed to fetch feedback');
    }

    const data = await response.json();
    return Array.isArray(data) ? data : [];
  }

  async updateFeedback(feedbackId: string, feedback: FeedbackCreate): Promise<Feedback> {
    const response = await fetch(`${API_BASE_URL}/api/feedback/${feedbackId}`, {
      method: 'PUT',
      headers: this.getAuthHeaders(),
      body: JSON.stringify(feedback)
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.detail || 'Failed to update feedback');
    }

    return response.json();
  }

  async deleteFeedback(feedbackId: string): Promise<void> {
    const response = await fetch(`${API_BASE_URL}/api/feedback/${feedbackId}`, {
      method: 'DELETE',
      headers: this.getAuthHeaders()
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.detail || 'Failed to delete feedback');
    }
  }

  async getFeedback(feedbackId: string): Promise<Feedback> {
    const response = await fetch(`${API_BASE_URL}/api/feedback/${feedbackId}`, {
      method: 'GET',
      headers: this.getAuthHeaders()
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.detail || 'Failed to fetch feedback');
    }

    return response.json();
  }

  // Admin methods
  async getAllFeedbacks(statusFilter?: string): Promise<FeedbackWithUser[]> {
    const params = new URLSearchParams();
    if (statusFilter && statusFilter !== 'all') {
      params.append('status_filter', statusFilter);
    }
    
    const response = await fetch(`${API_BASE_URL}/api/admin/feedbacks?${params}`, {
      method: 'GET',
      headers: this.getAuthHeaders()
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.detail || 'Failed to fetch feedbacks');
    }

    return response.json();
  }

  async getFeedbackAdmin(feedbackId: string): Promise<FeedbackWithUser> {
    const response = await fetch(`${API_BASE_URL}/api/admin/feedbacks/${feedbackId}`, {
      method: 'GET',
      headers: this.getAuthHeaders()
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.detail || 'Failed to fetch feedback');
    }

    return response.json();
  }

  async updateFeedbackAdmin(feedbackId: string, update: { status?: string; admin_notes?: string }): Promise<Feedback> {
    const response = await fetch(`${API_BASE_URL}/api/admin/feedbacks/${feedbackId}`, {
      method: 'PATCH',
      headers: this.getAuthHeaders(),
      body: JSON.stringify(update)
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.detail || 'Failed to update feedback');
    }

    return response.json();
  }

  async deleteFeedbackAdmin(feedbackId: string): Promise<void> {
    const response = await fetch(`${API_BASE_URL}/api/admin/feedbacks/${feedbackId}`, {
      method: 'DELETE',
      headers: this.getAuthHeaders()
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.detail || 'Failed to delete feedback');
    }
  }
}

export const feedbackService = new FeedbackService();
